import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, CalendarPlus, Search } from "lucide-react";

const nomeCliente = (c) => [c.nome, c.cognome].filter(Boolean).join(" ").trim() || "Senza nome";

export default function AppuntamentoDialog({ onClose }) {
  const qc = useQueryClient();
  const { data: clienti } = useQuery({ queryKey: ["clienti"], queryFn: async () => (await api.get("/clienti")).data });
  const { data: cal } = useQuery({ queryKey: ["calendari"], queryFn: async () => (await api.get("/calendari")).data });
  const calendari = cal?.calendari || [];
  const mio = calendari.find((c) => c.mio);
  const [calendarioId, setCalendarioId] = useState("");
  const scelto = calendari.find((c) => c.user_id === calendarioId)
    || calendari.find((c) => c.user_id === cal?.default_user_id) || mio || calendari[0];
  const [form, setForm] = useState({ titolo: "", data: "", durata_min: 60, luogo: "", descrizione: "", avvisa: 0 });
  const [cliente, setCliente] = useState(null);
  const [q, setQ] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const query = q.trim().toLowerCase();
  const trovati = (clienti || []).filter((c) => !query || nomeCliente(c).toLowerCase().includes(query)).slice(0, 30);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const scegli = (c) => {
    setCliente(c); setListOpen(false); setQ("");
    if (!form.titolo.trim()) set("titolo", `Appuntamento — ${nomeCliente(c)}`);
  };

  const salva = async () => {
    if (!form.titolo.trim()) return toast.error("Indica il titolo dell'appuntamento");
    if (!form.data) return toast.error("Indica data e ora");
    setSaving(true);
    try {
      const { data } = await api.post("/calendar/events", {
        titolo: form.titolo.trim(), data: form.data, durata_min: Number(form.durata_min) || 60,
        calendario_user_id: scelto?.user_id || null,
        luogo: form.luogo, descrizione: form.descrizione, cliente_id: cliente?.id || null,
        avvisa_giorni: Number(form.avvisa) || null,
      });
      toast.success(`Appuntamento creato su ${data.calendario_label || "il tuo Google Calendar"}`);
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      if (data.htmlLink) window.open(data.htmlLink, "_blank");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella creazione");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="appuntamento-dialog">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4 border-t-2 border-[#C9A84C] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-[#1A2B4A] flex items-center gap-2">
            <CalendarPlus className="w-5 h-5 text-[#C9A84C]" /> Nuovo appuntamento
          </h3>
          <button data-testid="app-close" onClick={onClose} className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-5 h-5" /></button>
        </div>

        {calendari.length > 1 && (
          <div className="mb-4" data-testid="app-calendario-scelta">
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Su quale agenda</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {calendari.map((c) => (
                <button key={c.user_id} type="button" data-testid={`app-calendario-${c.user_id}`}
                  onClick={() => setCalendarioId(c.user_id)}
                  className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${scelto?.user_id === c.user_id
                    ? "bg-[#1A2B4A] text-white border-[#1A2B4A]"
                    : "bg-white text-[#1A2B4A] border-[#1A2B4A]/20 hover:border-[#C9A84C]"}`}>
                  <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: c.colore }} />
                  {c.label}
                </button>
              ))}
            </div>
            <p className="text-[10.5px] text-[#8a93a6] mt-1">
              L'appuntamento verrà creato su <b>{scelto?.label || "la tua agenda"}</b>.
            </p>
          </div>
        )}

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Cliente (facoltativo)</label>
        <div className="relative mt-1 mb-4">
          <div className="flex items-center border border-[#1A2B4A]/20 rounded-sm px-2.5 focus-within:border-[#C9A84C]">
            <Search className="w-4 h-4 text-[#4a5568] shrink-0" />
            <input data-testid="app-cliente-search"
              value={cliente && !listOpen ? nomeCliente(cliente) : q}
              onChange={(e) => { setQ(e.target.value); setListOpen(true); setCliente(null); }}
              onFocus={() => setListOpen(true)} placeholder="Cerca un cliente…"
              className="w-full px-2 py-2 bg-transparent text-sm focus:outline-none" />
            {(cliente || q) && (
              <button data-testid="app-cliente-clear" onClick={() => { setCliente(null); setQ(""); setListOpen(true); }}
                className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-4 h-4" /></button>
            )}
          </div>
          {listOpen && (
            <div data-testid="app-cliente-results" className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-[#1A2B4A]/20 rounded-sm shadow-lg">
              {trovati.length === 0 ? <div className="px-3 py-2 text-sm text-[#4a5568]">Nessun cliente trovato.</div>
                : trovati.map((c) => (
                  <button key={c.id} data-testid={`app-cliente-option-${c.id}`} onClick={() => scegli(c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#f9f8f5] border-b border-[#1A2B4A]/5 last:border-0">
                    {nomeCliente(c)}
                  </button>
                ))}
            </div>
          )}
        </div>

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Titolo</label>
        <input data-testid="app-titolo" value={form.titolo} onChange={(e) => set("titolo", e.target.value)}
          placeholder="es. Consulenza in studio"
          className="w-full mt-1 mb-4 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Data e ora</label>
            <input data-testid="app-data" type="datetime-local" value={form.data} onChange={(e) => set("data", e.target.value)}
              className="w-full mt-1 px-2.5 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Durata (minuti)</label>
            <select data-testid="app-durata" value={form.durata_min} onChange={(e) => set("durata_min", e.target.value)}
              className="w-full mt-1 px-2.5 py-2 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]">
              {[15, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} minuti</option>)}
            </select>
          </div>
        </div>

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Luogo</label>
        <input data-testid="app-luogo" value={form.luogo} onChange={(e) => set("luogo", e.target.value)}
          placeholder="es. Studio, videochiamata, Prefettura…"
          className="w-full mt-1 mb-4 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Note</label>
        <textarea data-testid="app-note" rows={2} value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)}
          placeholder="Note facoltative…"
          className="w-full mt-1 mb-5 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C] resize-none" />

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
          <button data-testid="app-save" onClick={salva} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Crea appuntamento
          </button>
        </div>
      </div>
    </div>
  );
}
